import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/authService';
import { colors } from '../styles/colors';
import RecruiterForm from './RecruiterForm';
import AddRecruiterForm from './AddRecruiterForm';
import ConfirmModal from './ConfirmModal';
import Pagination from './Pagination';
import { useDebounce, useAbortController } from '../hooks/useDebounce';

interface CompanyRecruitersProps {
  companyId: number;
  companyName: string;
  onClose: () => void;
}

interface RecruiterData {
  id: number;
  owner: boolean;
  active: boolean;
  pending: boolean;
  recruiter_id: number;
  email: string;
  current_sign_in_at: string | null;
  name: string | null;
  publish_opportunity: boolean;
  candidates_database_access: boolean;
}

const CompanyRecruiters: React.FC<CompanyRecruitersProps> = ({ companyId, companyName, onClose }) => {
  const [recruiters, setRecruiters] = useState<RecruiterData[]>([]);
  const [filteredRecruiters, setFilteredRecruiters] = useState<RecruiterData[]>([]);
  const [paginatedRecruiters, setPaginatedRecruiters] = useState<RecruiterData[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const itemsPerPage = 5;
  const [openActionMenu, setOpenActionMenu] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [recruiterToDelete, setRecruiterToDelete] = useState<RecruiterData | null>(null);
  const [editingRecruiter, setEditingRecruiter] = useState<RecruiterData | null>(null);

  useEffect(() => {
    fetchRecruiters();
  }, [companyId]);

  useEffect(() => {
    applySearch();
  }, [recruiters, debouncedSearch]);

  useEffect(() => {
    applyPagination();
  }, [filteredRecruiters, currentPage]);

  const fetchRecruiters = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/companies/${companyId}/recruiters`);
      setRecruiters(Array.isArray(response.data) ? response.data : response.data.recruiters || []);
    } catch (error: any) {
      toast.error('Erro ao carregar recrutadores');
    } finally {
      setLoading(false);
    }
  };

  const applySearch = () => {
    if (!debouncedSearch.trim()) {
      setFilteredRecruiters(recruiters);
      return;
    }
    
    const filtered = recruiters.filter(recruiter => 
      (recruiter.name && recruiter.name.toLowerCase().includes(debouncedSearch.toLowerCase())) ||
      recruiter.email.toLowerCase().includes(debouncedSearch.toLowerCase())
    );
    
    setFilteredRecruiters(filtered);
  };

  const applyPagination = () => {
    const total = Math.ceil(filteredRecruiters.length / itemsPerPage);
    setTotalPages(total);
    
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setPaginatedRecruiters(filteredRecruiters.slice(startIndex, endIndex));
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
  };

  const toggleActionMenu = (recruiterId: number) => {
    setOpenActionMenu(openActionMenu === recruiterId ? null : recruiterId);
  };

  const handleEdit = (recruiter: RecruiterData) => {
    setEditingRecruiter(recruiter);
    setShowForm(true);
    setOpenActionMenu(null);
  };

  const handleAdd = () => {
    setShowAddForm(true);
  };

  const handleDelete = (recruiter: RecruiterData) => {
    setRecruiterToDelete(recruiter);
    setShowConfirmModal(true);
    setOpenActionMenu(null);
  };

  const confirmDelete = async () => {
    if (recruiterToDelete) {
      try {
        await api.delete(`/companies/${companyId}/recruiters/${recruiterToDelete.id}`);
        toast.success('Recrutador removido com sucesso!');
        setCurrentPage(1);
        await fetchRecruiters();
      } catch (error: any) {
        toast.error('Erro ao remover recrutador');
      }
    }
    setShowConfirmModal(false);
    setRecruiterToDelete(null);
  };

  const cancelDelete = () => {
    setShowConfirmModal(false);
    setRecruiterToDelete(null);
  };

  const handleFormSave = () => {
    setShowForm(false);
    setEditingRecruiter(null);
    fetchRecruiters();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingRecruiter(null);
  };

  const handleAddFormSave = () => {
    setShowAddForm(false);
    fetchRecruiters();
  };

  const handleAddFormCancel = () => {
    setShowAddForm(false);
  };



  return (
    <div style={styles.overlay} onClick={() => setOpenActionMenu(null)}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>Recrutadores - {companyName}</h2>
          <div style={styles.headerActions}>
            <button onClick={handleAdd} style={styles.addBtn}>
              Adicionar Recrutador
            </button>
            <button onClick={onClose} style={styles.closeBtn}>×</button>
          </div>
        </div>
        
        <form onSubmit={handleSearch} style={styles.searchForm}>
          <input
            type="text"
            placeholder="Buscar por nome ou email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={styles.searchInput}
          />
          <button type="submit" style={styles.searchBtn}>
            Buscar
          </button>
        </form>
        
        <div style={styles.content}>
          {loading ? (
            <div style={styles.loading}>Carregando...</div>
          ) : filteredRecruiters.length === 0 ? (
            <div style={styles.empty}>Nenhum recrutador encontrado</div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Recrutador</th>
                  <th style={styles.thCenter}>Owner</th>
                  <th style={styles.thCenter}>Ativo</th>
                  <th style={styles.thCenter}>Pendente</th>
                  <th style={styles.thCenter}>Publicar Oportunidade</th>
                  <th style={styles.thCenter}>Base de Candidatos</th>
                  <th style={styles.thCenter}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {paginatedRecruiters.map((item) => (
                  <tr key={item.id} style={styles.tr}>
                    <td style={styles.recruiterTd}>
                      <div>
                        <div style={styles.recruiterName}>{item.name || item.email}</div>
                        <div style={styles.recruiterEmail}>{item.email}</div>
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={item.owner ? styles.yesBadge : styles.noBadge}>
                        {item.owner ? 'Sim' : 'Não'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={item.active ? styles.yesBadge : styles.noBadge}>
                        {item.active ? 'Sim' : 'Não'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={item.pending ? styles.yesBadge : styles.noBadge}>
                        {item.pending ? 'Sim' : 'Não'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={item.publish_opportunity ? styles.yesBadge : styles.noBadge}>
                        {item.publish_opportunity ? 'Sim' : 'Não'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={item.candidates_database_access ? styles.yesBadge : styles.noBadge}>
                        {item.candidates_database_access ? 'Sim' : 'Não'}
                      </span>
                    </td>
                    <td style={styles.tdActions}>
                      <div style={styles.actionMenuContainer}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleActionMenu(item.id);
                          }}
                          style={styles.actionMenuBtn}
                        >
                          ⋮
                        </button>
                        {openActionMenu === item.id && (
                          <div style={styles.actionMenu}>
                            <button
                              onClick={() => handleEdit(item)}
                              style={styles.actionMenuItem}
                            >
                              Editar
                            </button>
                            <button
                              onClick={() => handleDelete(item)}
                              style={{...styles.actionMenuItem, color: colors.error}}
                            >
                              Excluir
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          
          {!loading && totalPages > 1 && (
            <div style={styles.paginationContainer}>
              <div style={styles.paginationInfo}>
                Página {currentPage} de {totalPages} ({filteredRecruiters.length} total)
              </div>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>
      
      {showForm && editingRecruiter && (
        <RecruiterForm
          companyId={companyId}
          recruiter={editingRecruiter}
          onSave={handleFormSave}
          onCancel={handleFormCancel}
        />
      )}
      
      {showAddForm && (
        <AddRecruiterForm
          companyId={companyId}
          onSave={handleAddFormSave}
          onCancel={handleAddFormCancel}
        />
      )}
      
      <ConfirmModal
        isOpen={showConfirmModal}
        title="Confirmar Remoção"
        message={`Tem certeza que deseja remover o recrutador ${recruiterToDelete?.name || recruiterToDelete?.email}?`}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        confirmText="Remover"
        cancelText="Cancelar"
      />
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '95%',
    maxWidth: '1400px',
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: `1px solid ${colors.border}`,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  addBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.secondary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '1rem',
  },
  title: {
    margin: 0,
    fontSize: '24px',
    color: colors.text,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '32px',
    cursor: 'pointer',
    color: colors.textLight,
    padding: '0',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    padding: '20px',
    overflowY: 'auto',
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    color: colors.textLight,
  },
  empty: {
    textAlign: 'center',
    padding: '40px',
    color: colors.textLight,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '12px',
    borderBottom: `2px solid ${colors.border}`,
    color: colors.text,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  thCenter: {
    textAlign: 'center',
    padding: '12px',
    borderBottom: `2px solid ${colors.border}`,
    color: colors.text,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  tr: {
    borderBottom: `1px solid ${colors.border}`,
  },
  td: {
    padding: '12px',
    color: colors.text,
    textAlign: 'center',
  },
  recruiterTd: {
    padding: '12px',
    color: colors.text,
    textAlign: 'left',
  },
  recruiterName: {
    fontWeight: 'bold',
    marginBottom: '4px',
  },
  recruiterEmail: {
    fontSize: '0.9rem',
    color: colors.textLight,
  },
  yesBadge: {
    backgroundColor: colors.success,
    color: colors.white,
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
  },
  noBadge: {
    backgroundColor: colors.error,
    color: colors.white,
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
  },
  tdActions: {
    padding: '12px',
    color: colors.text,
    textAlign: 'center',
    position: 'relative',
    minWidth: '80px',
  },
  actionMenuContainer: {
    position: 'relative',
  },
  actionMenuBtn: {
    padding: '8px 12px',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold',
  },
  actionMenu: {
    position: 'absolute',
    top: '100%',
    right: 0,
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    zIndex: 1001,
    minWidth: '120px',
  },
  actionMenuItem: {
    display: 'block',
    width: '100%',
    padding: '12px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    textAlign: 'left',
    cursor: 'pointer',
    fontSize: '14px',
    color: colors.text,
    borderBottom: `1px solid ${colors.border}`,
    fontFamily: 'inherit',
  },
  searchForm: {
    display: 'flex',
    gap: '12px',
    padding: '20px',
    borderBottom: `1px solid ${colors.border}`,
    backgroundColor: colors.background,
  },
  searchInput: {
    flex: 1,
    padding: '12px',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '14px',
    fontFamily: 'inherit',
  },
  searchBtn: {
    padding: '12px 24px',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'inherit',
  },
  paginationContainer: {
    marginTop: '1rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1rem',
  },
  paginationInfo: {
    textAlign: 'center',
    color: colors.textLight,
  },
};

export default CompanyRecruiters;
